import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import styles from './crossReference.module.css';
import { backURL } from '../../consts';
import { useGetCrossReferenceQuery } from '../../features/products/productsApiSlice';

const NO_PHOTO = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23f0f0f0'/%3E%3C/svg%3E";

const STEP = 340;       // на сколько px перематывает стрелка
const SPEED = 0.6;      // px за кадр (авто-прокрутка)

interface Props {
    productId: number | string;
}

const CrossReference: React.FC<Props> = ({ productId }) => {
    const { t } = useTranslation();
    const { data } = useGetCrossReferenceQuery(productId, { skip: !productId });
    const items = data ?? [];

    const trackRef = useRef<HTMLDivElement>(null);
    const pausedRef = useRef(false);

    // Авто-прокрутка («бегущая строка») + бесшовный цикл (список продублирован)
    useEffect(() => {
        if (items.length === 0) return;
        let raf = 0;
        const tick = () => {
            const el = trackRef.current;
            if (el && !pausedRef.current) {
                el.scrollLeft += SPEED;
                const half = el.scrollWidth / 2;
                if (half > 0 && el.scrollLeft >= half) el.scrollLeft -= half;
            }
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [items.length]);

    const scrollByStep = (dir: number) => {
        const el = trackRef.current;
        if (!el) return;
        const half = el.scrollWidth / 2;
        // бесшовная ручная перемотка у краёв (контент продублирован)
        if (dir < 0 && el.scrollLeft < STEP) el.scrollLeft += half;
        else if (dir > 0 && el.scrollLeft > half) el.scrollLeft -= half;
        el.scrollBy({ left: dir * STEP, behavior: 'smooth' });
    };

    if (items.length === 0) return null;

    const loop = [...items, ...items];

    return (
        <section className={styles.wrap}>
            <h2 className={styles.heading}>{t('product_features.cross_reference')}</h2>
            <div
                className={styles.marquee}
                onMouseEnter={() => { pausedRef.current = true; }}
                onMouseLeave={() => { pausedRef.current = false; }}
            >
                <button
                    type="button"
                    className={`${styles.arrow} ${styles.arrow_left}`}
                    onClick={() => scrollByStep(-1)}
                    aria-label="Назад"
                >
                    <FaChevronLeft />
                </button>

                <div className={styles.track} ref={trackRef}>
                    {loop.map((p, i) => (
                        <Link
                            to={`/products/${p.category_name}/${p.slug || p.product_id}`}
                            className={styles.card}
                            key={`${p.product_id}-${i}`}
                        >
                            <div className={styles.img_box}>
                                <img
                                    src={`${backURL}/${p.img}`}
                                    alt={`${p.brand} ${p.detail_number}`}
                                    onError={(e) => { (e.target as HTMLImageElement).src = NO_PHOTO; }}
                                />
                            </div>
                            <div className={styles.info}>
                                <span className={styles.brand}>{p.brand}</span>
                                {p.model && <span className={styles.model}>{p.model}</span>}
                                <span className={styles.part}>{p.detail_number}</span>
                            </div>
                        </Link>
                    ))}
                </div>

                <button
                    type="button"
                    className={`${styles.arrow} ${styles.arrow_right}`}
                    onClick={() => scrollByStep(1)}
                    aria-label="Вперёд"
                >
                    <FaChevronRight />
                </button>
            </div>
        </section>
    );
};

export default CrossReference;
